/**
 * Haptic feedback helpers (Capacitor).
 *
 * No-ops on web / non-native (guarded by isNativeApp) and never throws — if the
 * native plugin is missing the promise rejection is swallowed, so calling these
 * is always safe from any component.
 *
 * Use sparingly and meaningfully: a light tap on selection/navigation, a medium
 * tap on opening a surface, a notification buzz on a completed/!important action.
 * Over-buzzing feels cheap, not premium.
 */
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { isNativeApp } from './platform';

/** Light tap — row selection, navigation, chip press. */
export function hapticLight(): void {
  if (!isNativeApp()) return;
  Haptics.impact({ style: ImpactStyle.Light }).catch(() => {});
}

/** Medium tap — opening a sheet/modal, a more consequential press. */
export function hapticMedium(): void {
  if (!isNativeApp()) return;
  Haptics.impact({ style: ImpactStyle.Medium }).catch(() => {});
}

/** Selection tick — used while moving through discrete options (e.g. dismiss snap). */
export function hapticSelection(): void {
  if (!isNativeApp()) return;
  Haptics.selectionStart().catch(() => {});
}

/** Success / warning / error notification buzz. */
export function hapticNotify(type: 'success' | 'warning' | 'error' = 'success'): void {
  if (!isNativeApp()) return;
  const map = {
    success: NotificationType.Success,
    warning: NotificationType.Warning,
    error: NotificationType.Error,
  } as const;
  Haptics.notification({ type: map[type] }).catch(() => {});
}
